import { v4 as uuidv4 } from "uuid";

import {
  BookTimeSlotInput,
  ConfirmInboundDeliveryDateInput,
  ConfirmInboundDeliveryQuantitiesInput,
  CreateInboundDeliveryInput,
  EditInboundDeliveryInput,
  InboundDeliveryDetailsTimeOutput,
  InboundDeliveryDocumentCreateInput,
  InboundDeliveryDocumentEditInput,
  InboundDeliveryFragment,
  InboundDeliveryItemFragment,
  InboundDeliveryMaterialConfirmationInput,
  InboundDeliveryMaterialCreateInput,
  InboundDeliveryMaterialEditInput,
  InboundDeliveryMaterialSplitInput,
  InboundDeliveryTimeType,
  SplitInboundDeliveryInput,
  TimeSlotFragment,
} from "../models/autogenerated-deliveryService-gql/graphql";
import { IDeliveryItem } from "../models/deliveryItem";
import { IDocument } from "../models/document";
import { IGeneralInfo } from "../models/generalInfo";
import { ISavedProgress } from "../models/savedProgress";
import { isNullOrEmpty } from "./helper";

const mapToIDeliveryItems = (items: InboundDeliveryItemFragment[]) =>
  items
    .map((item) => mapToIDeliveryItem(item))
    .filter((item) => item !== undefined);

const mapToIDeliveryItem = (item: InboundDeliveryItemFragment) => {
  if (item.__typename !== "MaterialItem") return;

  return {
    id: item.id || uuidv4(),
    position: Number(item.number || 0),
    material: item.materialResolved?.name || "",
    materialId: item.materialResolved?.id || "",
    stockType: item.stockTypeResolved?.longName || "",
    stockTypeId: item.stockTypeResolved?.id || "",
    quantity: item.openQuantity || 0,
    unitOfMeasurement: item.unitOfMeasurementResolved?.unitOfMeasurement || "",
    itemCategory: item.category || "",
    batch: item.materialResolved?.batchesResolved?.join(",") || "",
    owner: item.ownerResolved?.name || "",
    serialNumbers:
      item.serialNumbers?.map((s) => s.serialNumber).join(",") || "",
  };
};

const mapToDeliveryDate = (times: InboundDeliveryDetailsTimeOutput[]) => {
  const date: string | undefined = times.find(
    (t) => t.type === InboundDeliveryTimeType.Pldeldate,
  )?.dateTime;

  return date ? new Date(date) : undefined;
};

const mapToSavedProgress = (
  delivery: InboundDeliveryFragment,
): ISavedProgress | undefined => {
  const {
    deliveryNote,
    deliveryNumber,
    documentNumber,
    documentType,
    locationResolved,
    items,
    times,
    documents,
  } = delivery;

  try {
    const generalInfo: IGeneralInfo = {
      deliveryNote: deliveryNote || "",
      deliveryNumber: deliveryNumber || "",
      documentNumber: documentNumber || "",
      documentType: documentType || null,
      location: locationResolved?.name || "",
      locationId: locationResolved?.id || "",
    };

    const deliveryItems: IDeliveryItem[] = mapToIDeliveryItems(items);

    const plannedDelivery: Date | undefined = mapToDeliveryDate(times);

    const documentItems: IDocument[] = documents.map((document) => {
      const filenameWithoutExtension = document.name?.substring(
        0,
        document.name?.lastIndexOf("."),
      );

      return {
        id: document.id || uuidv4(),
        name: filenameWithoutExtension || "",
        documentType: document.type || null,
        file: null,
      };
    });

    return { generalInfo, deliveryItems, plannedDelivery, documentItems };
  } catch (error) {
    console.error("Failed to build saved progress", error);
  }
};

const mapToCreateInboundDeliveryInput = (
  savedProgress: ISavedProgress,
): CreateInboundDeliveryInput | undefined => {
  const { generalInfo, deliveryItems, plannedDelivery, documentItems } =
    savedProgress;

  if (
    !generalInfo ||
    !deliveryItems ||
    !plannedDelivery ||
    !documentItems ||
    !generalInfo.documentType
  ) {
    console.warn("Missing data to build createInboundDeliveryDto");
    return;
  }

  try {
    const materialsWithoutHandlingUnit: InboundDeliveryMaterialCreateInput[] =
      deliveryItems.map((item) => ({
        batch: item.batch,
        category: item.itemCategory,
        materialId: item.materialId,
        number: item.position.toString(),
        owner: item.owner,
        requestedQuantity: Number(item.quantity),
        serialNumbers:
          item.serialNumbers.length > 0 ? item.serialNumbers.split(",") : [],
        stockTypeId: item.stockTypeId,
        unitOfMeasurement: item.unitOfMeasurement,
      }));

    const documents: InboundDeliveryDocumentCreateInput[] = documentItems.map(
      (doc) => {
        //We need to extract the ".pdf" from the file name and append it to the name field
        const fileType = doc.file?.name.split(".").pop();
        const fileExtension = fileType ? `.${fileType}` : "";

        return {
          file: doc.file,
          name: doc.name + fileExtension,
        };
      },
    );

    const input: CreateInboundDeliveryInput = {
      deliveryNote: generalInfo.deliveryNote,
      deliveryNumber: generalInfo.deliveryNumber,
      documentNumber: generalInfo.documentNumber,
      documentType: generalInfo.documentType,
      documents: documents,
      handlingUnits: [],
      locationId: generalInfo.locationId,
      materialsWithoutHandlingUnit: materialsWithoutHandlingUnit,
      plannedDeliveryTime: plannedDelivery,
    };

    return input;
  } catch (error) {
    console.error("Failed to build createInboundDeliveryInput", error);
  }
};

const mapToEditInboundDeliveryInput = (
  inboundDeliveryId: string,
  savedProgress: ISavedProgress,
): EditInboundDeliveryInput | undefined => {
  const { generalInfo, deliveryItems, plannedDelivery, documentItems } =
    savedProgress;

  if (
    !generalInfo ||
    !deliveryItems ||
    !plannedDelivery ||
    !documentItems ||
    !generalInfo.documentType
  ) {
    console.warn("Missing data to build createInboundDeliveryDto");
    return;
  }

  try {
    const materialsWithoutHandlingUnit: InboundDeliveryMaterialEditInput[] =
      deliveryItems.map((item) => ({
        batch: item.batch,
        category: item.itemCategory,
        materialId: item.materialId,
        number: item.position.toString(),
        owner: item.owner,
        requestedQuantity: Number(item.quantity),
        serialNumbers:
          item.serialNumbers.length > 0 ? item.serialNumbers.split(",") : [],
        stockTypeId: item.stockTypeId,
        unitOfMeasurement: item.unitOfMeasurement,
      }));

    const documents: InboundDeliveryDocumentEditInput[] = documentItems.map(
      (doc) => {
        //We need to extract the ".pdf" from the file name and append it to the name field
        const fileType = doc.file?.name.split(".").pop();
        const fileExtension = fileType ? `.${fileType}` : "";

        return {
          id: doc.id,
          file: doc.file,
          name: doc.name + fileExtension,
        };
      },
    );

    const input: EditInboundDeliveryInput = {
      deliveryNote: generalInfo.deliveryNote,
      documentNumber: generalInfo.documentNumber,
      documents,
      handlingUnits: [],
      inboundDeliveryId,
      materialsWithoutHandlingUnit: materialsWithoutHandlingUnit,
      plannedDeliveryTime: plannedDelivery,
    };

    return input;
  } catch (error) {
    console.error("Failed to build editInboundDeliveryInput", error);
  }
};

const mapToSplitInboundDeliveryInput = (
  inboundDeliveryId: string,
  newItems: IDeliveryItem[],
): SplitInboundDeliveryInput | undefined => {
  if (isNullOrEmpty(inboundDeliveryId) || newItems.length === 0) {
    console.warn("Missing data to build splitInboundDeliveryDto");
    return;
  }

  try {
    const materialsWithoutHandlingUnit: InboundDeliveryMaterialSplitInput[] =
      newItems.map((item) => ({
        id: item.id,
        requestedQuantity: item.quantity,
      }));

    const input: SplitInboundDeliveryInput = {
      handlingUnits: [],
      inboundDeliveryId,
      materialsWithoutHandlingUnit,
    };

    return input;
  } catch (error) {
    console.error("Failed to build splitInboundDeliveryInput", error);
  }
};

const mapToConfirmInboundDeliveryQuantitiesInput = (
  inboundDeliveryId: string,
  items: IDeliveryItem[],
): ConfirmInboundDeliveryQuantitiesInput | undefined => {
  if (isNullOrEmpty(inboundDeliveryId) || items.length === 0) {
    console.warn("Missing data to build confirmInboundDeliveryQuantitiesDto");
    return;
  }

  try {
    const materialsWithoutHandlingUnit: InboundDeliveryMaterialConfirmationInput[] =
      items.map((item) => ({
        batch: item.batch,
        id: item.id,
        owner: item.owner,
        requestedQuantity: Number(item.quantity),
        serialNumbers:
          item.serialNumbers.length > 0 ? item.serialNumbers.split(",") : [],
        stockTypeId: item.stockTypeId,
        unitOfMeasurement: item.unitOfMeasurement,
      }));

    const input: ConfirmInboundDeliveryQuantitiesInput = {
      handlingUnits: [],
      inboundDeliveryId,
      materialsWithoutHandlingUnit,
    };

    return input;
  } catch (error) {
    console.error(
      "Failed to build confirmInboundDeliveryQuantitiesInput",
      error,
    );
  }
};

const mapToConfirmInboundDeliveryDateInput = (
  inboundDeliveryId: string,
  plannedDeliveryDate: Date,
): ConfirmInboundDeliveryDateInput | undefined => {
  if (isNullOrEmpty(inboundDeliveryId) || !plannedDeliveryDate) {
    console.warn("Missing data to build confirmInboundDeliveryDateDto");
    return;
  }

  try {
    const input: ConfirmInboundDeliveryDateInput = {
      inboundDeliveryId,
      plannedDeliveryDate,
    };

    return input;
  } catch (error) {
    console.error("Failed to build confirmInboundDeliveryDateInput", error);
  }
};

const mapToBookTimeSlotInput = (
  inboundDeliveryId: string,
  selectedItems: IDeliveryItem[],
  timeSlot: TimeSlotFragment,
): BookTimeSlotInput | undefined => {
  if (isNullOrEmpty(inboundDeliveryId) || !timeSlot) {
    console.warn("Missing data to build bookTimeSlotDto");
    return;
  }

  try {
    const input: BookTimeSlotInput = {
      inboundDeliveryId,
      itemIds: selectedItems.map((item) => item.id),
      end: timeSlot.end,
      slotId: timeSlot.slotIds.at(0),
      start: timeSlot.start,
    };

    return input;
  } catch (error) {
    console.error("Failed to build bookTimeSlotInput", error);
  }
};

export {
  mapToBookTimeSlotInput,
  mapToConfirmInboundDeliveryDateInput,
  mapToConfirmInboundDeliveryQuantitiesInput,
  mapToCreateInboundDeliveryInput,
  mapToDeliveryDate,
  mapToEditInboundDeliveryInput,
  mapToIDeliveryItem,
  mapToIDeliveryItems,
  mapToSavedProgress,
  mapToSplitInboundDeliveryInput,
};
